import React, { useState } from 'react';
import useSEO from '../HomePage/useSEO';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import { getApiUrl } from '../../../config/api';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Textarea } from '@/Components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Components/ui/select';
import {
  Mail,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  MapPin,
  Globe,
  Clock,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Public Contact page — restyled to match the SearchJob.ai landing
 * theme (cream background, gradient heading accent, eyebrow pill,
 * rounded-3xl form card, pill submit button). The form logic and the
 * /api/contact submission are preserved exactly from the original.
 */
const ContactPublic = () => {
  useSEO({
    title: 'Contact SearchJob.ai — Book a Demo or Talk to Our Team',
    description:
      'Get in touch with SearchJob.ai. Book a demo, ask a question, or explore a partnership — our team replies within one business day.',
    canonical: 'https://searchjob.ai/hirewithus/contact',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(getApiUrl('/api/contact'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus({ type: 'success', message: result.message });
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus({
          type: 'error',
          message: result.error || 'Failed to send message. Please try again.',
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        type: 'error',
        message:
          'Network error. Please check your connection and try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16">
        <section className="relative overflow-hidden bg-background isolate">
          {/* Gradient mesh, matching the landing hero */}
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute top-[-15%] left-[8%] w-[55%] h-[55%] bg-primary/15 rounded-full blur-3xl" />
            <div className="absolute top-[5%] right-[5%] w-[45%] h-[45%] bg-emerald-500/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 lg:pt-14 pb-12 lg:pb-16">
            {/* Heading */}
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-5">
                <Sparkles className="w-3.5 h-3.5" />
                Contact us
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] text-foreground leading-[1.08] mb-5">
                Let&apos;s build your
                <br />
                <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  dream team.
                </span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Questions, a demo, or a partnership — tell us what you need and
                our team will get back within one business day.
              </p>
            </div>

            {/* Info + form */}
            <div className="grid lg:grid-cols-5 gap-6 lg:gap-8 max-w-5xl mx-auto">
              {/* Left — contact info */}
              <div className="lg:col-span-2 space-y-4">
                <InfoCard
                  icon={Mail}
                  label="Email us at"
                  value="contact@searchjob.ai"
                  highlight
                />
                <InfoCard icon={MapPin} label="Based in" value="Bangalore, India" />
                <InfoCard icon={Globe} label="On the web" value="www.searchjob.ai" />
                <div className="rounded-3xl border border-primary/25 bg-primary/5 p-5">
                  <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-1.5">
                    <Clock className="w-4 h-4" />
                    Fast response
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    We reply within one business day — usually much sooner.
                  </p>
                </div>
              </div>

              {/* Right — form card */}
              <div className="lg:col-span-3">
                <div className="bg-card border border-border/70 rounded-3xl p-6 md:p-8 shadow-[0_20px_60px_-25px_hsl(var(--primary)/0.35)]">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Enter your full name"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="Enter your email address"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="subject">Subject *</Label>
                      <Select
                        value={formData.subject}
                        onValueChange={(value) =>
                          setFormData({ ...formData, subject: value })
                        }
                        required
                      >
                        <SelectTrigger id="subject">
                          <SelectValue placeholder="Select a subject" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="technical-issue">
                            Technical Issue
                          </SelectItem>
                          <SelectItem value="billing-question">
                            Billing Question
                          </SelectItem>
                          <SelectItem value="feature-request">
                            Feature Request
                          </SelectItem>
                          <SelectItem value="general-inquiry">
                            General Inquiry
                          </SelectItem>
                          <SelectItem value="partnership">Partnership</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        placeholder="Describe your issue or question..."
                      />
                    </div>

                    {submitStatus && (
                      <div
                        className={cn(
                          'p-3 rounded-xl text-sm font-medium text-center border',
                          submitStatus.type === 'success'
                            ? 'bg-green-50 text-green-800 border-green-300'
                            : 'bg-red-50 text-red-800 border-red-300'
                        )}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {submitStatus.type === 'success' ? (
                            <CheckCircle2 className="w-4 h-4" />
                          ) : (
                            <AlertCircle className="w-4 h-4" />
                          )}
                          {submitStatus.message}
                        </div>
                      </div>
                    )}

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full !bg-primary !text-primary-foreground hover:!bg-primary/90 font-semibold !rounded-full shadow-lg shadow-primary/25"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending…
                        </>
                      ) : (
                        'Send Message'
                      )}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

function InfoCard({ icon: Icon, label, value, highlight = false }) {
  return (
    <div className="flex items-center gap-3.5 rounded-3xl border border-border/70 bg-card p-4 shadow-sm">
      <span className="w-11 h-11 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </span>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
          {label}
        </div>
        <div
          className={`text-sm font-bold truncate ${
            highlight ? 'text-primary' : 'text-foreground'
          }`}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

export default ContactPublic;
